import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { hillTargetsApiClient } from '../services/hillTargetsApiClient';
import { resolveHillTarget } from '../components/conversation/chatdag/utils/resolveHillTarget';
import type { HillTargetOut, HillTargetSet, HillForkRequest, ResolvedHillTarget } from '../types/hill';

// Module-level stable empty list — `data ?? []` would mint a fresh array each
// render while loading, churning `resolve`'s identity → the ChatDagCanvas useMemo
// would rebuild the whole DAG every render. A stable ref keeps it quiet.
const EMPTY_TARGETS: HillTargetOut[] = [];

/** SP3a read + SP3b writes — fetch the session's hill targets, expose the
 * persona→spine→trail resolver, and setTarget(PUT)/forkTarget(POST fork)
 * mutations that refresh the same query cache entry. */
export function useHillTargets(sessionId: string | null | undefined) {
  const qc = useQueryClient();
  const key = ['hill-targets', sessionId];
  const { data } = useQuery(
    key,
    () => hillTargetsApiClient.getHillTargets(sessionId as string),
    { enabled: !!sessionId, staleTime: 10_000 },
  );
  const targets: HillTargetOut[] = data ?? EMPTY_TARGETS;
  const resolve = useCallback(
    (personaId: string | undefined, spineRootMessageId: string | undefined): ResolvedHillTarget | null =>
      resolveHillTarget(targets, personaId, spineRootMessageId),
    [targets],
  );
  const putM = useMutation(
    (payload: HillTargetSet) => hillTargetsApiClient.putHillTarget(sessionId as string, payload),
    { onSuccess: (fresh) => qc.setQueryData(key, fresh) },
  );
  const forkM = useMutation(
    (body: HillForkRequest) => hillTargetsApiClient.forkHillTarget(sessionId as string, body),
    { onSuccess: (res) => qc.setQueryData(key, res.hill_targets) },
  );
  return {
    targets,
    resolve,
    setTarget: putM.mutateAsync,
    forkTarget: forkM.mutateAsync,
    isWriting: putM.isLoading || forkM.isLoading,
  };
}
